import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { fetchSessionAssessmentStatus } from "../api";
import { type Sleep, defaultSleep } from "./sleep";

export type PollSessionAssessmentStatusOptions = {
	intervalMs?: number;
	timeoutMs?: number;
	onProgress?: (completed: number, total: number) => void;
	sleep?: Sleep;
};

export type PollSessionAssessmentStatusResult = {
	allComplete: boolean;
	completed: number;
	total: number;
};

const DEFAULT_INTERVAL_MS = 5000;
const DEFAULT_TIMEOUT_MS = 90000;

// Immediate first poll (no wait if already graded); fetch errors are swallowed, only the timeout ends the loop.
export const pollSessionAssessmentStatus = async (
	sessionId: number,
	aiItemIds: number[],
	options: PollSessionAssessmentStatusOptions = {},
): Promise<PollSessionAssessmentStatusResult> => {
	const {
		intervalMs = DEFAULT_INTERVAL_MS,
		timeoutMs = DEFAULT_TIMEOUT_MS,
		onProgress,
		sleep = defaultSleep,
	} = options;

	const total = aiItemIds.length;
	if (total === 0) {
		onProgress?.(0, 0);
		return { allComplete: true, completed: 0, total: 0 };
	}

	const deadline = Date.now() + timeoutMs;
	let completed = 0;
	let lastReported = -1;
	let isFirstPoll = true;

	while (true) {
		if (!isFirstPoll) {
			await sleep(intervalMs);
		}
		isFirstPoll = false;

		try {
			const response = await fetchSessionAssessmentStatus(sessionId, aiItemIds);
			completed = response.items.filter(
				(item) => item.status === "COMPLETE",
			).length;
			if (completed !== lastReported) {
				onProgress?.(completed, total);
				lastReported = completed;
			}
			if (completed === total) {
				return { allComplete: true, completed, total };
			}
		} catch (error) {
			captureErrorToSentry(
				new Error("Error polling session assessment status"),
				{ error: serializeErrorForSentry(error), sessionId },
			);
		}

		if (Date.now() >= deadline) {
			return { allComplete: false, completed, total };
		}
	}
};
